
/**
 * UserChatWidget.jsx  —  User-facing floating chat widget
 *
 * ROOT-CAUSE FIXES vs previous version
 * ─────────────────────────────────────
 * 1. chatId was never stored correctly.
 *    `startOrResumeChat` returned `{ chatId, ticketNumber, ... }` but the
 *    widget read `res.data.chat?._id ?? res.data._id` — both undefined —
 *    resulting in localStorage.setItem("support_chat_id", "undefined").
 *    The user then joined a socket room called "undefined" and could never
 *    send or receive real messages.
 *    FIX: parse all response shapes; never store falsy / "undefined" values.
 *
 * 2. Messages disappeared on page refresh.
 *    GET /chat/:chatId/messages was restricted to staff only (403 for users).
 *    FIX (backend): controller now allows the chat owner to read their own
 *    messages.  FIX (frontend): widget now also calls GET /chat/mine on mount
 *    to restore the session without relying on localStorage alone.
 *
 * 3. Socket reconnect didn't re-join the room.
 *    When a user had a stored chatId, `user_reconnect` was only emitted when
 *    the widget was opened — not when the socket itself reconnected after a
 *    brief network interruption.
 *    FIX: listen for the socket `connect` event (fires on initial connect AND
 *    every automatic reconnect) and re-emit `user_reconnect` / `join_room`.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import {
  FaPaperPlane,
  FaUserCircle,
  FaHeadset,
  FaCircle,
  FaCheckDouble,
  FaInbox,
} from "react-icons/fa";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import "./UserChatPanel.css";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const formatDate = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return formatTime(iso);
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

// ─── ChatListItem ──────────────────────────────────────────────────────────────
const ChatListItem = ({ chat, active, unread, onClick }) => (
  <button
    className={`acp-chat-item ${active ? "acp-chat-item--active" : ""}`}
    onClick={() => onClick(chat)}
  >
    <div className="acp-chat-avatar">
      <FaUserCircle />
      <span className={`acp-status-dot acp-status-dot--${chat.status}`} />
    </div>
    <div className="acp-chat-meta">
      <div className="acp-chat-header-row">
        <span className="acp-chat-user">{chat.userId}</span>
        <span className="acp-chat-time">{formatDate(chat.updatedAt)}</span>
      </div>
      <div className="acp-chat-preview-row">
        <span className="acp-chat-preview">
          {chat.lastMessage || "No messages yet"}
        </span>
        {unread > 0 && <span className="acp-unread-badge">{unread}</span>}
      </div>
    </div>
  </button>
);

// ─── ConversationPane ──────────────────────────────────────────────────────────
const ConversationPane = ({
  chat,
  messages,
  onSend,
  onClose,
  agentName,
  userTyping,
}) => {
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, userTyping]);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText("");
  };

  const grouped = messages.reduce((acc, msg) => {
    const label = new Date(msg.createdAt).toLocaleDateString([], {
      month: "long",
      day: "numeric",
    });
    (acc[label] = acc[label] || []).push(msg);
    return acc;
  }, {});

  return (
    <div className="acp-conversation">
      <div className="acp-conv-header">
        <div className="acp-conv-user-info">
          <FaUserCircle className="acp-conv-avatar" />
          <div>
            <p className="acp-conv-name">{chat.userId}</p>
            <p className="acp-conv-status">
              <FaCircle className="acp-online-dot" /> Active now
            </p>
          </div>
        </div>
        <div className="acp-conv-actions">
          <button
            className="acp-close-chat-btn"
            onClick={() => onClose(chat._id)}
            title="Close this chat"
          >
            Close Chat
          </button>
        </div>
      </div>

      <div className="acp-messages">
        {Object.entries(grouped).map(([label, msgs]) => (
          <React.Fragment key={label}>
            <div className="acp-date-label">{label}</div>
            {msgs.map((msg) => {
              const isAgent = msg.senderType === "agent";
              return (
                <div
                  key={msg._id || msg.tempId}
                  className={`acp-msg-row ${isAgent ? "acp-msg-row--agent" : "acp-msg-row--user"}`}
                >
                  {!isAgent && (
                    <div className="acp-avatar acp-avatar--user">
                      <FaUserCircle />
                    </div>
                  )}
                  <div className="acp-bubble-wrap">
                    {!isAgent && (
                      <span className="acp-sender-name">{chat.userId}</span>
                    )}
                    <div
                      className={`acp-bubble ${isAgent ? "acp-bubble--agent" : "acp-bubble--user"}`}
                    >
                      {msg.text}
                    </div>
                    <div
                      className={`acp-time ${isAgent ? "acp-time--right" : ""}`}
                    >
                      {formatTime(msg.createdAt)}
                      {isAgent && (
                        <FaCheckDouble
                          className={`acp-tick ${msg.read ? "acp-tick--read" : ""}`}
                        />
                      )}
                    </div>
                  </div>
                  {isAgent && (
                    <div className="acp-avatar acp-avatar--agent">
                      <FaHeadset />
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}

        {userTyping && (
          <div className="acp-msg-row acp-msg-row--user">
            <div className="acp-avatar acp-avatar--user">
              <FaUserCircle />
            </div>
            <div className="acp-bubble acp-bubble--user acp-bubble--typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="acp-input-bar">
        <textarea
          className="acp-textarea"
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={`Reply as ${agentName}…`}
        />
        <button className="acp-send-btn" onClick={send} disabled={!text.trim()}>
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );
};

// ─── EmptyState ────────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="acp-empty">
    <FaInbox className="acp-empty-icon" />
    <p>Select a conversation to start replying</p>
  </div>
);

// ─── UserChatPanel (agent/admin side) ─────────────────────────────────────────
const UserChatPanel = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);

  // FIX: Keep activeChat in a ref as well so socket callbacks always see the
  // current value without needing to be re-registered (avoids stale closures).
  const [activeChat, setActiveChat] = useState(null);
  const activeChatRef = useRef(null);
  const setActiveChatBoth = (chat) => {
    activeChatRef.current = chat;
    setActiveChat(chat);
  };

  const [messages, setMessages] = useState([]);
  const [unreadMap, setUnreadMap] = useState({});
  const [userTyping, setUserTyping] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const socketRef = useRef(null);
  const typingTimer = useRef(null);

  const agentId = user?._id;
  const agentName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Agent"
    : "Agent";

  // ── Fetch chat list ──────────────────────────────────────────────────────
  const fetchChats = useCallback(async () => {
    try {
      const res = await api.get(`/chat`, { params: { status: statusFilter } });
      setChats(res.data.chats || []);
    } catch (err) {
      console.error("[AgentChatPanel] fetchChats:", err);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  // ── Socket setup (mount once per agentId) ───────────────────────────────
  useEffect(() => {
    if (!agentId) return;

    const socket = io(SOCKET_URL, {
      auth: { userId: agentId },
      transports: ["websocket"],
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on("receive_message", (msg) => {
      const cid = msg.chatId?.toString();
      const currentChatId = activeChatRef.current?._id?.toString();

      // Lenient: treat null/undefined currentChatId as "no active chat"
      // so messages always update the sidebar preview even when no chat is open.
      if (currentChatId && cid && currentChatId === cid) {
        // ── Active conversation: append, dedupe by _id or tempId ──────────
        setMessages((prev) => {
          if (prev.find((m) => m._id?.toString() === msg._id?.toString()))
            return prev;
          return [...prev.filter((m) => m.tempId !== msg.tempId), msg];
        });
        // Mark the incoming user message as read
        if (msg.senderType !== "agent") {
          socket.emit("mark_read", { chatId: cid, messageIds: [msg._id] });
        }
      } else {
        // ── Different chat: increment unread badge ────────────────────────
        setUnreadMap((prev) => ({ ...prev, [cid]: (prev[cid] || 0) + 1 }));
      }

      // Update sidebar preview for the relevant chat regardless
      setChats((prev) =>
        prev.map((c) =>
          c._id?.toString() === cid
            ? { ...c, lastMessage: msg.text, updatedAt: msg.createdAt }
            : c,
        ),
      );
    });

    socket.on("typing", ({ isTyping }) => {
      setUserTyping(isTyping);
      clearTimeout(typingTimer.current);
      if (isTyping)
        typingTimer.current = setTimeout(() => setUserTyping(false), 4000);
    });

    socket.on("message_read", ({ messageId }) => {
      setMessages((prev) =>
        prev.map((m) => (m._id === messageId ? { ...m, read: true } : m)),
      );
    });

    return () => {
      socket.disconnect();
      clearTimeout(typingTimer.current);
    };
  }, [agentId]);

  // ── Open a conversation ──────────────────────────────────────────────────
  const openChat = async (chat) => {
    if (activeChatRef.current) {
      socketRef.current?.emit("leave_room", {
        chatId: activeChatRef.current._id,
      });
    }

    setActiveChatBoth(chat);
    setUnreadMap((prev) => ({ ...prev, [chat._id]: 0 }));

    try {
      const res = await api.get(`/chat/${chat._id}/messages`);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error("[AgentChatPanel] loadMessages:", err);
    }

    socketRef.current?.emit("join_room", { chatId: chat._id });
  };

  // ── Send reply ───────────────────────────────────────────────────────────
  const handleSend = (text) => {
    if (!activeChatRef.current || !text.trim()) return;

    const tempId = `tmp-${Date.now()}`;

    // Optimistic append
    setMessages((prev) => [
      ...prev,
      {
        tempId,
        text,
        senderType: "agent",
        createdAt: new Date().toISOString(),
        read: false,
      },
    ]);

    // FIX: field name was `message` but socket handler reads `text`.
    // Now sending `text` to match chatSocket.js (which also accepts `message`
    // as a fallback, but `text` is canonical).
    socketRef.current?.emit("send_message", {
      chatId: activeChatRef.current._id,
      text, // ← was `message: text` — this was the agent send bug
      tempId,
      senderType: "agent",
    });

    setChats((prev) =>
      prev.map((c) =>
        c._id === activeChatRef.current._id ? { ...c, lastMessage: text } : c,
      ),
    );
  };

  // ── Close a chat session ─────────────────────────────────────────────────
  const handleCloseChat = async (chatId) => {
    try {
      await api.patch(`/chat/${chatId}/close`);
      setChats((prev) => prev.filter((c) => c._id !== chatId));
      if (activeChatRef.current?._id === chatId) {
        setActiveChatBoth(null);
        setMessages([]);
      }
    } catch (err) {
      console.error("[AgentChatPanel] closeChat:", err);
    }
  };

  // ── Guard ────────────────────────────────────────────────────────────────
  if (!user || (user.role !== "admin" && user.role !== "agent")) {
    return (
      <div className="acp-access-denied">
        <p>You do not have access to the agent panel.</p>
      </div>
    );
  }

  return (
    <div className="acp-root">
      <aside className="acp-sidebar">
        <div className="acp-sidebar-header">
          <div className="acp-agent-info">
            <div className="acp-agent-avatar">
              <FaHeadset />
            </div>
            <div>
              <p className="acp-agent-name">{agentName}</p>
              <p className="acp-agent-role">{user.role}</p>
            </div>
          </div>
          <div className="acp-filter-tabs">
            {["open", "closed"].map((s) => (
              <button
                key={s}
                className={`acp-tab ${statusFilter === s ? "acp-tab--active" : ""}`}
                onClick={() => {
                  setStatusFilter(s);
                  setActiveChatBoth(null);
                }}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="acp-chat-list">
          {chats.length === 0 && (
            <p className="acp-no-chats">No {statusFilter} chats</p>
          )}
          {chats.map((chat) => (
            <ChatListItem
              key={chat._id}
              chat={chat}
              active={activeChat?._id === chat._id}
              unread={unreadMap[chat._id] || 0}
              onClick={openChat}
            />
          ))}
        </div>
      </aside>

      <main className="acp-main">
        {activeChat ? (
          <ConversationPane
            chat={activeChat}
            messages={messages}
            onSend={handleSend}
            onClose={handleCloseChat}
            agentName={agentName}
            userTyping={userTyping}
          />
        ) : (
          <EmptyState />
        )}
      </main>
    </div>
  );
};

export default UserChatPanel;
